// Shared kit for the culture-styled city centres (buildings_styled*.ts):
// tier/style types, the team-grey constants, the voxel builders (box / shell /
// slab / disc / dedupe), the per-tier size table and the build context.
// Pure functions of their arguments — no randomness.

import type { BuildingStyleId, StylePalette } from './styles.js';
import type { BlockVoxel } from './types.js';

/** City-centre tiers: 1 village / outpost, 2 town, 3 capital. */
export type CentreTier = 1 | 2 | 3;
export const CENTRE_TIERS: readonly CentreTier[] = [1, 2, 3];

/** Styles that get their own centres (everything but `neutral`). */
export type CentreStyleId = Exclude<BuildingStyleId, 'neutral'>;

export const TEAM_LIT = '#d2d2d2';
export const TEAM_FOLD = '#a8a8a8';

export type V = BlockVoxel;

export function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  team = false,
): V[] {
  const out: V[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++)
        out.push(team ? { x, y, z, color, team: true } : { x, y, z, color });
  return out;
}

/** Hollow rectangular ring, one layer thick per y, centred on the origin: |x|,|z| <= half. */
export function shell(
  half: number,
  y0: number,
  h: number,
  color: (x: number, y: number, z: number) => string,
): V[] {
  const out: V[] = [];
  for (let y = y0; y < y0 + h; y++)
    for (let x = -half; x <= half; x++)
      for (let z = -half; z <= half; z++)
        if (Math.abs(x) === half || Math.abs(z) === half)
          out.push({ x, y, z, color: color(x, y, z) });
  return out;
}

/** Solid square slab, |x|,|z| <= half. */
export function slab(half: number, y: number, color: string): V[] {
  return box(-half, y, -half, 2 * half + 1, 1, 2 * half + 1, color);
}

export function disc(r: number, y: number, color: string): V[] {
  const out: V[] = [];
  const lim = r * r + r * 0.5;
  for (let x = -Math.ceil(r); x <= Math.ceil(r); x++)
    for (let z = -Math.ceil(r); z <= Math.ceil(r); z++)
      if (x * x + z * z <= lim) out.push({ x, y, z, color });
  return out;
}

/** Later voxels win at a shared position (same rule as the geometry cache). */
export function dedupe(blocks: readonly V[]): V[] {
  const m = new Map<string, V>();
  for (const b of blocks) m.set(`${b.x},${b.y},${b.z}`, b);
  return [...m.values()].sort((a, b) => a.y - b.y || a.x - b.x || a.z - b.z);
}

export interface TierSpec {
  /** Outer half-extent: plinth, roof overhang and columns stay within it. */
  readonly B: number;
  /** Wall shell half-extent. */
  readonly wallHalf: number;
  readonly wallH: number;
}

export const TIER_SPECS: Readonly<Record<CentreTier, TierSpec>> = {
  1: { B: 3, wallHalf: 2, wallH: 3 },
  2: { B: 4, wallHalf: 3, wallH: 3 },
  3: { B: 5, wallHalf: 4, wallH: 4 },
};

/** First wall layer: plinth is y=0, base course y=1. */
export const WALL_Y = 2;

export interface Ctx {
  readonly style: CentreStyleId;
  readonly p: StylePalette;
  readonly tier: CentreTier;
  readonly spec: TierSpec;
}

export interface Roofed {
  readonly blocks: V[];
  readonly topY: number;
}
